import type { ApplicationFormModelProgress } from '../useApplicationFormProgress';
import { TelemetryLogger } from '~/Utils/Logging/TelemetryLogger';
import type {
    ApplicationFormProgressState as ApplicationFormProgressStateV1,
    PersonalInfo as PersonalInfoV1,
} from './v1/ApplicationFormProgressState';
import type {
    ApplicationFormProgressState as ApplicationFormProgressStateV2,
    PersonalInfo as PersonalInfoV2,
} from './v2/ApplicationFormProgressState';
import type {
    ApplicationFormProgressState as ApplicationFormProgressStateV3,
    PersonalInfo as PersonalInfoV3,
} from './v3/ApplicationFormProgressState';
import type {
    ApplicationFormProgressState as ApplicationFormProgressStateV4,
    PersonalInfo as PersonalInfoV4,
} from './v4/ApplicationFormProgressState';
import { map as mapV1toV4 } from './v4/MapperV1';
import { map as mapV2toV4 } from './v4/MapperV2';
import { map as mapV3toV4 } from './v4/MapperV3';

const activeApplicationFormVersion = 'v4';
type ActiveApplicationFormProgressState = ApplicationFormProgressStateV4;

type ApplicationFormModelState =
    | ApplicationFormProgressStateV1
    | ApplicationFormProgressStateV2
    | ApplicationFormProgressStateV3
    | ApplicationFormProgressStateV4;

type PersonalInfo = PersonalInfoV1 | PersonalInfoV2 | PersonalInfoV3 | PersonalInfoV4;

const isVersionOne = (model: ApplicationFormModelState): model is ApplicationFormProgressStateV1 =>
    model.version === undefined;

const isVersionTwo = (model: ApplicationFormModelState): model is ApplicationFormProgressStateV2 =>
    model.version === 'v2';

const isVersionThree = (model: ApplicationFormModelState): model is ApplicationFormProgressStateV3 =>
    model.version === 'v3';

const isVersionFour = (model: ApplicationFormModelState): model is ApplicationFormProgressStateV4 =>
    model.version === 'v4';

const map = (model: ApplicationFormModelState, personalInfo: PersonalInfo | undefined) => {
    if (isVersionOne(model)) {
        return mapV1toV4(model, personalInfo);
    }

    if (isVersionTwo(model)) {
        return mapV2toV4(model, personalInfo);
    }

    if (isVersionThree(model)) {
        return mapV3toV4(model, personalInfo);
    }

    if (isVersionFour(model)) {
        model.order.personalInfo = personalInfo;

        return model;
    }

    TelemetryLogger.errorMessage(
        `Unrecognized application form progress state version '${(model as ApplicationFormModelState).version}'. Clearing state.`
    );

    setApplicationFormProgressState(undefined);

    return undefined;
};

export const applicationFormProgressKey = 'applicationFormProgress';

export const getRawApplicationFormState = (): ApplicationFormModelState | undefined => {
    const modelJson = localStorage.getItem(applicationFormProgressKey);

    if (!modelJson) {
        return undefined;
    }

    return JSON.parse(modelJson) as ApplicationFormModelState;
};

export const getApplicationFormProgressState = (): ApplicationFormModelProgress | undefined => {
    const model = getRawApplicationFormState();

    if (!model) {
        return undefined;
    }

    const personalInfoJson = sessionStorage.getItem(applicationFormProgressKey);
    const personalInfo = personalInfoJson ? (JSON.parse(personalInfoJson) as PersonalInfo) : undefined;

    return map(model, personalInfo);
};

export const setApplicationFormProgressState = (progressState: ApplicationFormModelProgress | undefined) => {
    if (!progressState) {
        localStorage.removeItem(applicationFormProgressKey);
        sessionStorage.removeItem(applicationFormProgressKey);

        return;
    }

    if (progressState.order) {
        const personalInfo: ActiveApplicationFormProgressState['order']['personalInfo'] =
            progressState.order.personalInfo;

        personalInfo && sessionStorage.setItem(applicationFormProgressKey, JSON.stringify(personalInfo));
    }

    const state: ActiveApplicationFormProgressState = {
        ...progressState,
        order: {
            ...progressState.order,
            personalInfo: undefined,
        },
        version: activeApplicationFormVersion,
    };

    localStorage.setItem(applicationFormProgressKey, JSON.stringify(state));
};
